import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./Filter.css";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import { IoIosArrowDown } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import Slider from "@mui/material/Slider";
import { Link, useSearchParams } from "react-router-dom";
import { getCategories, getShopFilterOptions } from "../../../api/storefront";

const COLOR_TRUNCATE = 24;

/** Normalize ?category= value for comparison with category name/slug (hyphens ↔ spaces). */
function normalizeCategoryKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/['\u2018\u2019]/g, "")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ");
}

function splitCsvParam(val) {
  if (val == null || val === "") return [];
  return String(val)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function mergeShopSearch(searchParams, overrides) {
  const next = new URLSearchParams(searchParams);
  Object.entries(overrides).forEach(([k, v]) => {
    if (v === null || v === undefined || v === "") next.delete(k);
    else next.set(k, String(v));
  });
  return next;
}

/** Gender "All": drop ?gender= and legacy ?category=Mens|Womens shop shortcuts. */
function shopGenderAllSearch(searchParams) {
  const next = mergeShopSearch(searchParams, { gender: null });
  const cat = searchParams.get("category") || "";
  let nk = "";
  try {
    nk = normalizeCategoryKey(decodeURIComponent(cat.trim()) || cat);
  } catch (_) {
    nk = normalizeCategoryKey(cat);
  }
  if (nk === "mens" || nk === "men" || nk === "womens" || nk === "women") {
    next.delete("category");
    next.delete("categoryId");
  }
  return next;
}

function normalizeCategoryParents(categories) {
  const ids = new Set(categories.map((c) => c.id));
  return categories.map((c) => ({
    ...c,
    parent_id: c.parent_id != null && !ids.has(c.parent_id) ? null : c.parent_id,
  }));
}

/** Pre-order tree walk: roots first (parent_id null), children by sort_order/name. */
function buildCategoryDisplayOrder(categories) {
  if (!categories?.length) return [];
  const normalized = normalizeCategoryParents(categories);
  const byParent = new Map();
  for (const c of normalized) {
    const pid = c.parent_id != null ? c.parent_id : null;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(c);
  }
  const sortFn = (a, b) => {
    const so = (a.sort_order ?? 0) - (b.sort_order ?? 0);
    if (so !== 0) return so;
    return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
  };
  for (const arr of byParent.values()) arr.sort(sortFn);
  function walk(parentId, depth) {
    const kids = byParent.get(parentId) || [];
    const out = [];
    for (const c of kids) {
      out.push({ node: c, depth });
      out.push(...walk(c.id, depth + 1));
    }
    return out;
  }
  return walk(null, 0);
}

/** Shop filters: URL drives /api/products (?category, color, size, minPrice, maxPrice). */
const Filter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const shopCategoryFilter = searchParams.get("category") || "";

  const [priceLocal, setPriceLocal] = useState([0, 10000]);
  const [categories, setCategories] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);

  const [categorySearch, setCategorySearch] = useState("");
  const [colorSearch, setColorSearch] = useState("");
  const [colorsShowAll, setColorsShowAll] = useState(false);

  const [panels, setPanels] = useState({
    gender: true,
    categories: true,
    color: false,
    size: false,
    price: false,
  });

  // Re-fetch categories whenever gender filter changes so the list only shows
  // categories that have products matching the selected gender.
  const genderForCategoryFetch = useMemo(() => {
    const g = (searchParams.get('gender') || '').trim().toLowerCase();
    if (g === 'male' || g === 'female') return g;
    return null;
  }, [searchParams]);

  useEffect(() => {
    getCategories(genderForCategoryFetch).then((list) => {
      setCategories(Array.isArray(list) ? list : []);
    });
  }, [genderForCategoryFetch]);

  /** Same scope as product list + gender (price bounds / facet context). */
  const facetQuery = useMemo(() => {
    const cid = searchParams.get("categoryId");
    const cat = searchParams.get("category");
    const qs = new URLSearchParams();
    if (cid != null && String(cid).trim() !== "") qs.set("categoryId", String(cid).trim());
    else if (cat != null && String(cat).trim() !== "") qs.set("category", cat);
    const g = (searchParams.get("gender") || "").trim().toLowerCase();
    if (g === "male" || g === "female") qs.set("gender", g);
    return qs.toString();
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    getShopFilterOptions(facetQuery).then((opts) => {
      if (cancelled) return;
      if (!opts) {
        setFilterOptions({
          min_price_rupees: 0,
          max_price_rupees: 10000,
          colors: [],
          sizes: [],
        });
        return;
      }
      setFilterOptions(opts);
    });
    return () => {
      cancelled = true;
    };
  }, [facetQuery]);

  const minBound = filterOptions?.min_price_rupees ?? 0;
  const maxBound = Math.max(
    (filterOptions?.max_price_rupees ?? 10000),
    minBound + 1
  );

  useEffect(() => {
    const mp = searchParams.get("minPrice");
    const xp = searchParams.get("maxPrice");
    if (mp != null && mp !== "" && xp != null && xp !== "") {
      const a = Number(mp);
      const b = Number(xp);
      if (!Number.isNaN(a) && !Number.isNaN(b)) {
        let lo = Math.max(minBound, Math.min(a, maxBound));
        let hi = Math.max(minBound, Math.min(b, maxBound));
        if (lo > hi) [lo, hi] = [hi, lo];
        setPriceLocal([lo, hi]);
        return;
      }
    }
    setPriceLocal([minBound, maxBound]);
  }, [searchParams, minBound, maxBound, filterOptions]);

  const selectedColors = useMemo(
    () => splitCsvParam(searchParams.get("color")),
    [searchParams]
  );
  const selectedSizes = useMemo(
    () => splitCsvParam(searchParams.get("size")),
    [searchParams]
  );

  const priceFilterActive = useMemo(() => {
    const mp = searchParams.get("minPrice");
    const xp = searchParams.get("maxPrice");
    if (mp == null || mp === "" || xp == null || xp === "") return false;
    const a = Number(mp);
    const b = Number(xp);
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    return a > minBound || b < maxBound;
  }, [searchParams, minBound, maxBound]);

  useEffect(() => {
    setPanels((prev) => ({
      gender: true,
      categories: true,
      color: prev.color || selectedColors.length > 0,
      size: prev.size || selectedSizes.length > 0,
      price: prev.price || priceFilterActive,
    }));
  }, [selectedColors.length, selectedSizes.length, priceFilterActive]);

  useEffect(() => {
    if (colorSearch.trim()) setColorsShowAll(true);
  }, [colorSearch]);

  const urlCategoryNorm = useMemo(() => {
    const raw = (shopCategoryFilter || "").trim();
    if (!raw) return "";
    try {
      return normalizeCategoryKey(decodeURIComponent(raw));
    } catch (_) {
      return normalizeCategoryKey(raw);
    }
  }, [shopCategoryFilter]);

  const genderParam = (searchParams.get("gender") || "").trim().toLowerCase();
  const legacyMenCategory = urlCategoryNorm === "mens" || urlCategoryNorm === "men";
  const legacyWomenCategory = urlCategoryNorm === "womens" || urlCategoryNorm === "women";
  const genderMenSelected =
    genderParam === "male" || (!genderParam && legacyMenCategory);
  const genderWomenSelected =
    genderParam === "female" || (!genderParam && legacyWomenCategory);
  const genderAllSelected = !genderMenSelected && !genderWomenSelected;

  const categoryRowActive = (c) => {
    const name = normalizeCategoryKey(c.name);
    const slug = normalizeCategoryKey(c.slug || "");
    return urlCategoryNorm && (urlCategoryNorm === name || urlCategoryNorm === slug);
  };

  const setParams = useCallback(
    (overrides) => {
      setSearchParams(mergeShopSearch(searchParams, overrides));
    },
    [searchParams, setSearchParams]
  );

  const toggleColor = (name) => {
    const next = new Set(selectedColors);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    const joined = [...next].join(",");
    setParams({ color: joined || null });
  };

  const toggleSize = (size) => {
    const next = new Set(selectedSizes);
    if (next.has(size)) next.delete(size);
    else next.add(size);
    const joined = [...next].join(",");
    setParams({ size: joined || null });
  };

  const handlePriceCommit = (_e, newValue) => {
    if (!Array.isArray(newValue)) return;
    setParams({
      minPrice: String(newValue[0]),
      maxPrice: String(newValue[1]),
    });
  };

  const clearFacetFilters = () => {
    setParams({
      color: null,
      size: null,
      minPrice: null,
      maxPrice: null,
    });
  };

  const removeColorChip = useCallback(
    (name) => {
      const next = selectedColors.filter((c) => c !== name);
      setParams({ color: next.length ? next.join(",") : null });
    },
    [selectedColors, setParams]
  );

  const removeSizeChip = useCallback(
    (size) => {
      const next = selectedSizes.filter((s) => s !== size);
      setParams({ size: next.length ? next.join(",") : null });
    },
    [selectedSizes, setParams]
  );

  const clearPriceChip = useCallback(() => {
    setParams({ minPrice: null, maxPrice: null });
  }, [setParams]);

  const dynamicColors = useMemo(
    () => (filterOptions?.colors?.length ? filterOptions.colors : []),
    [filterOptions]
  );
  const dynamicSizes = useMemo(
    () => (filterOptions?.sizes?.length ? filterOptions.sizes : []),
    [filterOptions]
  );

  const categoryDisplayList = useMemo(() => {
    if (!categories?.length) return [];
    return buildCategoryDisplayOrder(categories);
  }, [categories]);

  const filteredCategoryRows = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    if (!q) return categoryDisplayList;
    return categoryDisplayList.filter(({ node: c }) => {
      const name = String(c.name || "").toLowerCase();
      const slug = String(c.slug || "").toLowerCase();
      return name.includes(q) || slug.includes(q);
    });
  }, [categoryDisplayList, categorySearch]);

  const filteredColors = useMemo(() => {
    const q = colorSearch.trim().toLowerCase();
    if (!q) return dynamicColors;
    return dynamicColors.filter((c) => String(c).toLowerCase().includes(q));
  }, [dynamicColors, colorSearch]);

  const colorSearchActive = Boolean(colorSearch.trim());
  const colorsTruncated =
    !colorSearchActive && !colorsShowAll && filteredColors.length > COLOR_TRUNCATE;
  const colorsToRender = colorsTruncated
    ? filteredColors.slice(0, COLOR_TRUNCATE)
    : filteredColors;

  const displayCategoryLabel = useMemo(() => {
    const raw = (shopCategoryFilter || "").trim();
    if (!raw) return "";
    try {
      return decodeURIComponent(raw);
    } catch (_) {
      return raw;
    }
  }, [shopCategoryFilter]);

  const activeChips = useMemo(() => {
    const chips = [];
    const showCategoryChip =
      urlCategoryNorm &&
      displayCategoryLabel &&
      !legacyMenCategory &&
      !legacyWomenCategory;

    if (showCategoryChip) {
      chips.push({
        key: `cat-${urlCategoryNorm}`,
        label: displayCategoryLabel,
        onRemove: () => setParams({ category: null, categoryId: null }),
      });
    }
    if (genderMenSelected) {
      chips.push({
        key: "gender-men",
        label: "Men",
        onRemove: () => {
          if (genderParam === "male" || genderParam === "female") {
            setParams({ gender: null });
          } else if (legacyMenCategory || legacyWomenCategory) {
            setParams({ category: null, categoryId: null });
          }
        },
      });
    } else if (genderWomenSelected) {
      chips.push({
        key: "gender-women",
        label: "Women",
        onRemove: () => {
          if (genderParam === "male" || genderParam === "female") {
            setParams({ gender: null });
          } else if (legacyMenCategory || legacyWomenCategory) {
            setParams({ category: null, categoryId: null });
          }
        },
      });
    }
    selectedColors.forEach((col) => {
      chips.push({
        key: `color-${col}`,
        label: col,
        onRemove: () => removeColorChip(col),
      });
    });
    selectedSizes.forEach((sz) => {
      chips.push({
        key: `size-${sz}`,
        label: `Size ${sz}`,
        onRemove: () => removeSizeChip(sz),
      });
    });
    if (priceFilterActive) {
      const mp = searchParams.get("minPrice");
      const xp = searchParams.get("maxPrice");
      chips.push({
        key: "price",
        label: `₹${mp} – ₹${xp}`,
        onRemove: clearPriceChip,
      });
    }
    return chips;
  }, [
    genderMenSelected,
    genderWomenSelected,
    genderParam,
    legacyMenCategory,
    legacyWomenCategory,
    urlCategoryNorm,
    displayCategoryLabel,
    selectedColors,
    selectedSizes,
    priceFilterActive,
    searchParams,
    setParams,
    removeColorChip,
    removeSizeChip,
    clearPriceChip,
  ]);

  const setPanel = (name, expanded) => {
    setPanels((p) => ({ ...p, [name]: expanded }));
  };

  return (
    <div>
      <div className="filterSection">
        {activeChips.length > 0 ? (
          <div className="filterActiveChips" role="list" aria-label="Active filters">
            {activeChips.map((ch) => (
              <span key={ch.key} className="filterChip" role="listitem">
                <span className="filterChipLabel">{ch.label}</span>
                <button
                  type="button"
                  className="filterChipRemove"
                  aria-label={`Remove ${ch.label}`}
                  onClick={ch.onRemove}
                >
                  <IoClose size={16} />
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <div className="filterGender">
          <Accordion
            expanded={panels.gender}
            onChange={(_e, exp) => setPanel("gender", exp)}
            disableGutters
            elevation={0}
          >
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel-gender-content"
              id="panel-gender-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">Gender</h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <div className="genderOptions">
                <Link
                  to={{
                    pathname: "/shop",
                    search: shopGenderAllSearch(searchParams).toString(),
                  }}
                  className={`genderButton ${genderAllSelected ? "selected" : ""}`}
                >
                  All
                </Link>
                <Link
                  to={{
                    pathname: "/shop",
                    search: mergeShopSearch(searchParams, { gender: "male", category: null, categoryId: null }).toString(),
                  }}
                  className={`genderButton ${genderMenSelected ? "selected" : ""}`}
                >
                  Men
                </Link>
                <Link
                  to={{
                    pathname: "/shop",
                    search: mergeShopSearch(searchParams, { gender: "female", category: null, categoryId: null }).toString(),
                  }}
                  className={`genderButton ${genderWomenSelected ? "selected" : ""}`}
                >
                  Women
                </Link>
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
        <div className="filterCategories">
          <Accordion
            expanded={panels.categories}
            onChange={(_e, exp) => setPanel("categories", exp)}
            disableGutters
            elevation={0}
          >
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel-categories-content"
              id="panel-categories-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">Product Categories</h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {categories === null ? (
                <p className="filterCategoriesHint">Loading categories…</p>
              ) : categories.length === 0 ? (
                <p className="filterCategoriesHint">
                  No categories loaded. Ensure the API is running and categories exist in Admin (or import CSV).
                </p>
              ) : (
                <>
                  <div className="filterPaneSearch filterPaneSearchCompact">
                    <input
                      type="search"
                      placeholder="Search categories"
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      aria-label="Search categories"
                      autoComplete="off"
                    />
                  </div>
                  <div className="filterScrollPane">
                    {filteredCategoryRows.length === 0 ? (
                      <p className="filterCategoriesHint">No categories match.</p>
                    ) : (
                      filteredCategoryRows.map(({ node: c, depth }) => (
                        <Link
                          key={c.id}
                          to={{
                            pathname: "/shop",
                            search: mergeShopSearch(searchParams, { category: c.name, categoryId: null }).toString(),
                          }}
                          className={`filterCategoryLink ${categoryRowActive(c) ? "active" : ""}`}
                          style={{ paddingLeft: `${8 + depth * 14}px` }}
                        >
                          {c.name}
                          {c.product_count != null && c.product_count > 0 ? (
                            <span className="filterCategoryCount"> ({c.product_count})</span>
                          ) : null}
                        </Link>
                      ))
                    )}
                  </div>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </div>
        <div className="filterColors">
          <Accordion
            expanded={panels.color}
            onChange={(_e, exp) => setPanel("color", exp)}
            disableGutters
            elevation={0}
          >
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel-colors-content"
              id="panel-colors-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">Color</h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {filterOptions === null ? (
                <p className="filterCategoriesHint">Loading…</p>
              ) : dynamicColors.length === 0 ? (
                <p className="filterCategoriesHint">No colors in this view.</p>
              ) : (
                <>
                  <div className="filterPaneSearch filterPaneSearchCompact">
                    <input
                      type="search"
                      placeholder="Search colors"
                      value={colorSearch}
                      onChange={(e) => setColorSearch(e.target.value)}
                      aria-label="Search colors"
                      autoComplete="off"
                    />
                  </div>
                  <div className="filterScrollPane">
                    <div className="filterColorNames">
                      {colorsToRender.map((colorName) => (
                        <button
                          key={colorName}
                          type="button"
                          className={`colorNameButton ${selectedColors.includes(colorName) ? "selected" : ""}`}
                          onClick={() => toggleColor(colorName)}
                        >
                          {colorName}
                        </button>
                      ))}
                    </div>
                  </div>
                  {colorsTruncated ? (
                    <button
                      type="button"
                      className="filterShowMore"
                      onClick={() => setColorsShowAll(true)}
                    >
                      Show all ({filteredColors.length})
                    </button>
                  ) : null}
                  {!colorsTruncated && !colorSearchActive && colorsShowAll && filteredColors.length > COLOR_TRUNCATE ? (
                    <button
                      type="button"
                      className="filterShowMore"
                      onClick={() => setColorsShowAll(false)}
                    >
                      Show less
                    </button>
                  ) : null}
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </div>
        <div className="filterSizes">
          <Accordion
            expanded={panels.size}
            onChange={(_e, exp) => setPanel("size", exp)}
            disableGutters
            elevation={0}
          >
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel-sizes-content"
              id="panel-sizes-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">Sizes</h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {filterOptions === null ? (
                <p className="filterCategoriesHint">Loading…</p>
              ) : dynamicSizes.length === 0 ? (
                <p className="filterCategoriesHint">No sizes in this view.</p>
              ) : (
                <div className="sizeButtons">
                  {dynamicSizes.map((size) => (
                    <button
                      key={size}
                      type="button"
                      className={`sizeButton ${selectedSizes.includes(size) ? "selected" : ""}`}
                      onClick={() => toggleSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        </div>
        <div className="filterPrice">
          <Accordion
            expanded={panels.price}
            onChange={(_e, exp) => setPanel("price", exp)}
            disableGutters
            elevation={0}
          >
            <AccordionSummary
              expandIcon={<IoIosArrowDown size={20} />}
              aria-controls="panel-price-content"
              id="panel-price-header"
              sx={{ padding: 0, marginBottom: 2 }}
            >
              <h5 className="filterHeading">Price (from / day)</h5>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {filterOptions === null ? (
                <p className="filterCategoriesHint">Loading…</p>
              ) : (
                <>
                  <Slider
                    getAriaLabel={() => "Price range"}
                    value={priceLocal}
                    onChange={(_e, v) => Array.isArray(v) && setPriceLocal(v)}
                    onChangeCommitted={handlePriceCommit}
                    valueLabelDisplay="auto"
                    min={minBound}
                    max={maxBound}
                    valueLabelFormat={(v) => `₹${v}`}
                    sx={{
                      color: "black",
                      "& .MuiSlider-thumb": {
                        backgroundColor: "white",
                        border: "2px solid black",
                        width: 18,
                        height: 18,
                      },
                    }}
                  />
                  <div className="filterSliderPrice">
                    <div className="priceRange">
                      <p>
                        Min Price: <span>₹{priceLocal[0]}</span>
                      </p>
                      <p>
                        Max Price: <span>₹{priceLocal[1]}</span>
                      </p>
                    </div>
                  </div>
                  {(searchParams.get("color") ||
                    searchParams.get("size") ||
                    searchParams.get("minPrice") ||
                    searchParams.get("maxPrice")) && (
                    <button type="button" className="filterClearFacets" onClick={clearFacetFilters}>
                      Clear color, size &amp; price filters
                    </button>
                  )}
                </>
              )}
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default Filter;
